<script lang="ts">
	import { format } from 'svelte-i18n';
	// Design System Components
	import {
		SidepanelLayout,
		SidepanelTabs,
		SidepanelContent,
		ParameterCard,
	} from '@Shelly/shelly-design-system';
	// Stores
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { alarms } from 'src/stores/lists/scenes-derived';
	import { sidePanelState, sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	// Helpers
	import { handleSidePanelTabClick, alarmsTabsConfig } from '../../sidepanel-tabs-helpers';
	import { buildAlarmRoomsWithSensors } from 'src/components/Lists/list-helpers';
	import { _deepClone } from 'src/helpers/utilities';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	// Alarm-specific tab components
	import AlarmMain from './Tabs/AlarmMain.svelte';
	import AlarmDevices from './Tabs/AlarmDevices.svelte';
	import AlarmEvents from './Tabs/AlarmEvents.svelte';
	import AlarmInfo from './Tabs/AlarmInfo.svelte';
	// Components
	import AlarmWizard from 'src/components/AlarmZones/AlarmWizard/AlarmWizard.svelte';

	const ALARM_SIDE_PANEL_CONTENT_CONFIG = [AlarmMain, AlarmDevices, AlarmEvents, AlarmInfo];

	let tabIndex: number = 0;
	let tabs = alarmsTabsConfig;

	$: id = $sidePanelStateCombined?.id;
	$: alarm = $alarms[id];
	$: roomsWithSensors = [];
	$: missingDevices = [];
	$: hasPresence = false;
	let showAddEditAlarmModal = false;
	/**
	 * Snapshot of the alarm taken at the moment the edit modal is opened.
	 * Using a one-time deep clone prevents $alarms store polling from overwriting
	 * in-progress edits inside AlarmWizard.
	 */
	let editAlarmObject = null;

	$: isAlarmEnabled = typeof $alarms[id] !== 'undefined' ? $alarms[id]?._enabled : alarm?._enabled;

	$: name = alarm?._meta?.name || '';
	$: roomName = alarm?._meta?.roomn || '';

	/** Build roomsWithSensors and missingDevices — presence devices deduplicated with zone labels*/
	$: if (alarm) {
		({ roomsWithSensors, missingDevices, hasPresence } = buildAlarmRoomsWithSensors(
			alarm,
			$devices,
			$rooms,
		));
	}

	$: textUnderDeviceButtonTranslation = alarm
		? isAlarmEnabled
			? $format('label_action_alarm_disarm', { default: 'Disarm' })
			: $format('label_action_alarm_arm', { default: 'Arm' })
		: '';

	const onTabClick = (e: CustomEvent) => {
		const selectedTabData = e.detail;
		tabIndex = selectedTabData.index;
		tabs = handleSidePanelTabClick(tabs, tabIndex);
	};

	const resetActiveTab = () => {
		tabIndex = 0;
		tabs = alarmsTabsConfig;
	};

	/**
	 * Open the alarm edit wizard with a stable snapshot of the current alarm.
	 * Snapshotting here rather than passing a reactive expression to AlarmWizard
	 * prevents $alarms store updates from resetting wizard state mid-edit.
	 */
	const handleAlarmEdit = () => {
		editAlarmObject = _deepClone(alarm);
		showAddEditAlarmModal = true;
	};

	$: !$sidePanelState.open ? resetActiveTab() : null;
</script>

{#if typeof alarm === 'object'}
	<SidepanelLayout
		title={name}
		subtitle={roomName}
		settingsIcon={canManageHomeEntities($homes.list) ? 'fat fa-pen' : undefined}
		onSettings={canManageHomeEntities($homes.list) ? handleAlarmEdit : undefined}
		hideSettings={!canManageHomeEntities($homes.list)}
	>
		<svelte:component
			this={ALARM_SIDE_PANEL_CONTENT_CONFIG?.[tabIndex]}
			{id}
			{alarm}
			{isAlarmEnabled}
			{textUnderDeviceButtonTranslation}
			{roomsWithSensors}
			{missingDevices}
			{hasPresence}
		/>

		<SidepanelTabs slot="tabs" {tabs} on:click={onTabClick} />
	</SidepanelLayout>
{:else if id !== '-1'}
	<SidepanelLayout title={$format('title_deleted_alarm', { default: 'Deleted alarm' })}>
		<SidepanelContent>
			<ParameterCard
				icon="fas fa-shield-halved"
				title={$format('label_not_found_el', {
					default: 'Not found element',
				})}
				description={'ID:' + id}
				size="m"
			/>
		</SidepanelContent>
	</SidepanelLayout>
{/if}

{#if showAddEditAlarmModal && editAlarmObject}
	<AlarmWizard
		bind:id
		bind:showAddEditAlarmModal
		alarm_object={editAlarmObject}
		on:close={() => (editAlarmObject = null)}
	/>
{/if}
